<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import BookingDetailsSection from 'dashboard/components/bookings/view/BookingDetailsSection.vue';
import {
  typeIcon as iconFor,
  statusColor as colorFor,
} from 'dashboard/components/bookings/bookingTypes';

const props = defineProps({
  booking: { type: Object, default: null },
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['update:show', 'close', 'delete', 'edit']);

const { t } = useI18n();

const typeIcon = computed(() => iconFor(props.booking?.booking_type));

const typeLabel = computed(() => {
  const key = props.booking?.booking_type?.toUpperCase();
  return t(`CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.TYPE_OPTIONS.${key}`);
});

const statusColor = computed(() => colorFor(props.booking?.status));

const statusLabel = computed(() => {
  const key = props.booking?.status?.toUpperCase();
  return t(`CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.STATUS_OPTIONS.${key}`);
});

const dateRange = computed(() => {
  const start = props.booking?.start_date;
  const end = props.booking?.end_date;
  if (!start) return '';
  const startStr = new Date(start).toLocaleDateString();
  if (!end) return startStr;
  return `${startStr} — ${new Date(end).toLocaleDateString()}`;
});

const amountDisplay = computed(() => {
  const amount = props.booking?.total_amount;
  const currency = props.booking?.currency;
  if (!amount) return '';
  return `${currency || 'AED'} ${Number(amount).toLocaleString()}`;
});

const createdAt = computed(() => {
  if (!props.booking?.created_at) return '';
  return new Date(props.booking.created_at * 1000).toLocaleDateString();
});

const details = computed(() => props.booking?.details || {});

const onClose = () => {
  emit('update:show', false);
  emit('close');
};

const onDelete = () => {
  emit('delete', props.booking?.id);
  onClose();
};

const onEdit = () => {
  emit('edit', props.booking);
  onClose();
};
</script>

<template>
  <woot-modal
    :show="show"
    :on-close="onClose"
    class="!items-start [&>div]:!top-12 [&>div]:sticky"
  >
    <div v-if="booking" class="flex w-full flex-col gap-5 px-6 py-6">
      <!-- Header -->
      <div class="flex items-start justify-between">
        <div class="flex items-start gap-3">
          <div
            class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-n-alpha-2 text-n-slate-11"
          >
            <span :class="typeIcon" class="size-5" />
          </div>
          <div>
            <h3 class="text-lg font-semibold text-n-slate-12">
              {{ booking.title }}
            </h3>
            <div class="mt-1 flex items-center gap-2 text-sm text-n-slate-11">
              <span>{{ typeLabel }}</span>
              <span
                v-if="booking.reference_number"
                class="font-mono text-n-slate-12"
              >
                #{{ booking.reference_number }}
              </span>
            </div>
          </div>
        </div>
        <span
          :class="statusColor"
          class="shrink-0 rounded-full px-3 py-1 text-xs font-medium"
        >
          {{ statusLabel }}
        </span>
      </div>

      <!-- Info Grid -->
      <div class="grid grid-cols-2 gap-4">
        <div v-if="dateRange" class="space-y-1">
          <p class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.START_DATE') }} /
            {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.END_DATE') }}
          </p>
          <p class="flex items-center gap-1 text-sm text-n-slate-12">
            <span class="i-lucide-calendar size-4 text-n-slate-11" />
            {{ dateRange }}
          </p>
        </div>
        <div v-if="amountDisplay" class="space-y-1">
          <p class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.TOTAL_AMOUNT') }}
          </p>
          <p class="text-sm font-semibold text-n-slate-12">
            {{ amountDisplay }}
          </p>
        </div>
        <div v-if="createdAt" class="space-y-1">
          <p class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.CREATED') }}
          </p>
          <p class="text-sm text-n-slate-12">{{ createdAt }}</p>
        </div>
        <div v-if="booking.user" class="space-y-1">
          <p class="text-xs font-medium text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.CREATED_BY') }}
          </p>
          <p class="text-sm text-n-slate-12">{{ booking.user.name }}</p>
        </div>
      </div>

      <BookingDetailsSection
        :booking-type="booking.booking_type"
        :details="details"
      />

      <!-- Attachments -->
      <div v-if="booking.files?.length" class="space-y-2">
        <p class="text-xs font-medium text-n-slate-11">
          {{ t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.FILES') }}
        </p>
        <div class="flex flex-wrap gap-2">
          <a
            v-for="file in booking.files"
            :key="file.id"
            :href="file.url"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center gap-2 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-blue-11 hover:bg-n-alpha-2"
          >
            <span class="i-lucide-paperclip size-4" />
            {{ file.filename }}
          </a>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-between border-t border-n-weak pt-4"
      >
        <div class="flex items-center gap-2">
          <NextButton
            ghost
            xs
            icon="i-lucide-pencil"
            :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.EDIT')"
            @click="onEdit"
          />
          <NextButton
            ghost
            xs
            ruby
            icon="i-lucide-trash-2"
            :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.DELETE')"
            @click="onDelete"
          />
        </div>
        <NextButton
          ghost
          :label="t('CONTACTS_LAYOUT.SIDEBAR.BOOKINGS.CLOSE')"
          @click="onClose"
        />
      </div>
    </div>
  </woot-modal>
</template>
